import React from 'react';
import { CalendarDays, Check, Sparkles } from 'lucide-react';
import { Input } from './ui/input';

export default function ServicePlanSelector({ plans, selectedPlanId, onSelect, customMonths, onCustomMonths, baseAmount }) {
  if (!plans.length) return null;
  const selectedPlan = plans.find((plan) => plan.id === selectedPlanId);
  return (
    <section className="mb-6 border-y border-slate-200 py-5" data-testid="service-plan-selector">
      <div className="flex items-end justify-between gap-4 mb-3">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Choose your protection plan</h3>
          <p className="text-xs text-slate-500">Select the coverage period that works for your property.</p>
        </div>
        {selectedPlan?.duration_months > 0 && <span className="hidden sm:inline text-xs font-semibold text-emerald-700" data-testid="service-plan-duration">{selectedPlan.duration_months} month cover</span>}
      </div>
      <div className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory" data-testid="service-plan-scroll-row">
        {plans.map((plan) => {
          const selected = plan.id === selectedPlanId;
          const multiplier = plan.is_custom_duration
            ? (Number(plan.monthly_multiplier) || 1) * Number(customMonths || plan.custom_min_months || 1)
            : Number(plan.price_multiplier) || 1;
          const price = plan.pricing_mode === 'fixed'
            ? Number(plan.fixed_amount) || 0
            : Math.max(Math.round((baseAmount || 0) * multiplier), Number(plan.minimum_amount) || 0);
          return (
            <button
              key={plan.id}
              type="button"
              onClick={() => onSelect(plan.id)}
              data-testid={`service-plan-${plan.code || plan.id}`}
              className={`relative shrink-0 snap-start w-44 min-h-36 rounded-lg border p-3 text-left transition-[border-color,background-color,transform,box-shadow] duration-150 ${selected ? 'border-emerald-600 bg-emerald-50 shadow-sm -translate-y-0.5' : 'border-slate-200 bg-white hover:border-emerald-300 hover:-translate-y-0.5'}`}
            >
              {plan.featured && <span className="absolute -top-2 left-3 inline-flex items-center gap-1 bg-emerald-600 px-2 py-0.5 text-[10px] font-bold text-white" data-testid={`service-plan-featured-${plan.code || plan.id}`}><Sparkles size={10} /> Popular</span>}
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-bold text-slate-900">{plan.name}</span>
                {selected && <span className="grid h-5 w-5 place-items-center rounded-full bg-emerald-600 text-white"><Check size={13} /></span>}
              </div>
              <p className="mt-2 min-h-8 text-[11px] leading-snug text-slate-500">{plan.description}</p>
              <div className="mt-3 flex items-center gap-1 text-xs font-semibold text-slate-600"><CalendarDays size={13} /> {plan.is_custom_duration ? 'Flexible duration' : plan.duration_months ? `${plan.duration_months} months` : 'Single visit'}</div>
              {baseAmount > 0 && <div className="mt-1 text-sm font-extrabold text-emerald-700" data-testid={`service-plan-price-${plan.code || plan.id}`}>₹{price.toLocaleString('en-IN')}</div>}
            </button>
          );
        })}
      </div>
      {selectedPlan?.is_custom_duration && (
        <div className="mt-3 max-w-xs" data-testid="service-plan-custom-duration-wrap">
          <label htmlFor="service-plan-custom-duration" className="text-xs font-semibold text-slate-700">Coverage duration (months)</label>
          <Input id="service-plan-custom-duration" type="number" min={selectedPlan.custom_min_months || 1} max={selectedPlan.custom_max_months || 60} value={customMonths} onChange={(event) => onCustomMonths(event.target.value)} className="mt-1 h-10" data-testid="service-plan-custom-duration-input" />
          <p className="mt-1 text-[11px] text-slate-500">Choose {selectedPlan.custom_min_months || 1}–{selectedPlan.custom_max_months || 60} months.</p>
        </div>
      )}
      {selectedPlan?.features?.length > 0 && (
        <div className="mt-4 border-l-2 border-emerald-600 pl-3" data-testid="service-plan-benefits">
          <p className="text-xs font-bold text-slate-800">{selectedPlan.name} includes</p>
          <ul className="mt-1 grid gap-1 text-xs text-slate-600 sm:grid-cols-2">
            {selectedPlan.features.map((feature, index) => <li key={`${feature}-${index}`} className="flex items-center gap-1.5"><Check size={13} className="shrink-0 text-emerald-600" />{feature}</li>)}
          </ul>
        </div>
      )}
    </section>
  );
}